import {
  ActionIcon,
  Affix,
  Button,
  Drawer,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconMessageCircle,
  IconSend,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ComboboxItemPrice } from "~/types";

import classes from "./kiko-doctor-profile-assistant.module.scss";

type AssistantMessage = {
  id: string;
  role: "assistant" | "user";
  text: string;
};

type DoctorAssistantContext = {
  doctorId: string;
  profileId?: string;
  slug?: string;
  name?: string;
  specialization?: string;
  specialties?: {
    label: string;
    value: string;
  }[];
  location?: string;
  consultationModes?: string[];
  services?: ComboboxItemPrice[];
  partnerSlug?: string;
};

interface KikoDoctorProfileAssistantProps {
  doctor: DoctorAssistantContext;
  language: string;
  disabled?: boolean;
}

const SESSION_KEY_PREFIX = "laruche_kiko_doctor_session";

const copy = {
  en: {
    title: "Kiko booking assistant",
    launcher: "Need help booking?",
    intro: (doctorName?: string) =>
      `Hi, I am Kiko. I can help with ${
        doctorName ? `${doctorName}'s` : "this doctor's"
      } services, consultation options, and booking steps.`,
    placeholder: "Ask about this doctor or booking...",
    send: "Send",
    thinking: "Kiko is checking...",
    error: "Kiko could not respond right now. Please try again.",
    quickActions: [
      "What does this doctor treat?",
      "How much does it cost?",
      "Can I book online?",
      "Help me choose a service",
    ],
  },
  fr: {
    title: "Assistant de réservation Kiko",
    launcher: "Besoin d'aide ?",
    intro: (doctorName?: string) =>
      `Bonjour, je suis Kiko. Je peux vous aider avec les services, les options de consultation et les étapes de réservation ${
        doctorName ? `de ${doctorName}` : "de ce médecin"
      }.`,
    placeholder: "Posez une question sur ce médecin...",
    send: "Envoyer",
    thinking: "Kiko vérifie...",
    error: "Kiko ne peut pas répondre pour le moment. Veuillez réessayer.",
    quickActions: [
      "Que traite ce médecin ?",
      "Combien coûte la consultation ?",
      "Puis-je consulter en ligne ?",
      "Aidez-moi à choisir un service",
    ],
  },
};

const createId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getSessionKey = (doctorId: string) => `${SESSION_KEY_PREFIX}:${doctorId}`;

const getSessionId = (doctorId: string) => {
  try {
    const key = getSessionKey(doctorId);
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const sessionId = createId();
    window.sessionStorage.setItem(key, sessionId);
    return sessionId;
  } catch {
    return createId();
  }
};

const getLocalizedCopy = (language: string) =>
  language?.toLowerCase().startsWith("fr") ? copy.fr : copy.en;

const assistantResponseFromBody = (body: any, fallback: string) =>
  body?.data?.kikoResponse ||
  body?.data?.bookingConfirmation?.payment_link ||
  body?.error?.message ||
  fallback;

export const KikoDoctorProfileAssistant = ({
  doctor,
  language,
  disabled,
}: KikoDoctorProfileAssistantProps) => {
  const [opened, { open, close }] = useDisclosure(false);
  const text = useMemo(() => getLocalizedCopy(language), [language]);
  const introText = useMemo(
    () => text.intro(doctor.name),
    [doctor.name, text]
  );
  const [messages, setMessages] = useState<AssistantMessage[]>([
    {
      id: "intro",
      role: "assistant",
      text: introText,
    },
  ]);
  const [draft, setDraft] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const sessionIdRef = useRef<string>("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessages(current =>
      current.length === 1 && current[0].id === "intro"
        ? [{ id: "intro", role: "assistant", text: introText }]
        : current
    );
  }, [introText]);

  useEffect(() => {
    if (!doctor.doctorId) return;
    sessionIdRef.current = getSessionId(doctor.doctorId);
  }, [doctor.doctorId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages, isLoading]);

  const sendMessage = async (messageText: string) => {
    const trimmed = messageText.trim();
    if (!trimmed || isLoading || disabled) return;

    setDraft("");
    setIsLoading(true);
    setMessages(current => [
      ...current,
      { id: createId(), role: "user", text: trimmed },
    ]);

    try {
      const response = await fetch("/api/kiko-doctor-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmed,
          sessionId: sessionIdRef.current || getSessionId(doctor.doctorId),
          language,
          doctor: {
            ...doctor,
            specialties: doctor.specialties?.map(specialty => ({
              label: specialty.label,
              value: specialty.value,
            })),
            services: doctor.services?.map(service => ({
              label: service.label,
              price: service.price,
              originalPrice: service.originalPrice,
              currency: service.currency,
              duration: service.duration,
              category: service.category,
            })),
            pageUrl: window.location.href,
          },
        }),
      });
      const body = await response.json();
      if (body?.data?.sessionId) {
        sessionIdRef.current = body.data.sessionId;
        window.sessionStorage.setItem(
          getSessionKey(doctor.doctorId),
          body.data.sessionId
        );
      }

      setMessages(current => [
        ...current,
        {
          id: body?.data?.id || createId(),
          role: "assistant",
          text: assistantResponseFromBody(body, text.error),
        },
      ]);
    } catch {
      setMessages(current => [
        ...current,
        { id: createId(), role: "assistant", text: text.error },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!doctor.doctorId || disabled) return null;

  return (
    <>
      {!opened ? (
        <Affix position={{ bottom: 24, right: 24 }} className={classes.launcher}>
          <Button
            className={classes["launcher-button"]}
            radius="xl"
            size="md"
            leftSection={<IconSparkles size={18} />}
            onClick={open}>
            {text.launcher}
          </Button>
        </Affix>
      ) : null}

      <Drawer
        opened={opened}
        onClose={close}
        title={
          <Group gap="xs">
            <IconMessageCircle size={18} />
            <Text fw={600}>{text.title}</Text>
          </Group>
        }
        position="bottom"
        size="min(78vh, 680px)"
        radius="lg"
        overlayProps={{ backgroundOpacity: 0.25, blur: 2 }}
        closeButtonProps={{ icon: <IconX size={18} /> }}>
        <div className={classes["drawer-body"]}>
          <Stack className={classes.messages} gap="sm">
            {messages.map(message => (
              <Text
                key={message.id}
                className={`${classes.message} ${
                  message.role === "user"
                    ? classes["user-message"]
                    : classes["assistant-message"]
                }`}>
                {message.text}
              </Text>
            ))}

            <div className={classes["quick-actions"]}>
              {text.quickActions.map(action => (
                <Button
                  key={action}
                  type="button"
                  variant="light"
                  size="xs"
                  radius="xl"
                  onClick={() => sendMessage(action)}
                  disabled={isLoading}>
                  {action}
                </Button>
              ))}
            </div>

            {isLoading ? (
              <Group gap="xs" c="dimmed">
                <Loader size={14} />
                <Text fz={13}>{text.thinking}</Text>
              </Group>
            ) : null}
            <div ref={messagesEndRef} />
          </Stack>

          <form
            className={classes["input-row"]}
            onSubmit={event => {
              event.preventDefault();
              sendMessage(draft);
            }}>
            <Textarea
              autosize
              minRows={1}
              maxRows={4}
              value={draft}
              placeholder={text.placeholder}
              onChange={event => setDraft(event.currentTarget.value)}
              disabled={isLoading}
            />
            <ActionIcon
              type="submit"
              size={42}
              radius="xl"
              variant="filled"
              aria-label={text.send}
              disabled={!draft.trim() || isLoading}>
              <IconSend size={18} />
            </ActionIcon>
          </form>
        </div>
      </Drawer>
    </>
  );
};
